<template>
   <div class="listing-details-container">
      <!-- Header -->
      <div class="listing-header bg-primary text-white">
         <button @click="$router.go(-1)" class="btn btn-link text-white p-0">
            <i class="bi bi-arrow-left fs-5"></i>
         </button>
         <div class="header-text">
            <h6 class="fw-bold mb-0">Listing Details</h6>
            <p class="small opacity-75 mb-0">{{ _name }}</p>
         </div>
      </div>

      <!-- Main Content -->
      <div class="listing-content" :style="{ marginBottom: '100px' }">
         <!-- Image Section -->
         <div class="listing-image-container bg-light rounded-bottom-4" v-if="datarow.img">
            <img :src="$asset_path+img_path+'/'+datarow.img" 
                 :alt="datarow.name || 'Listing image'" 
                 class="img-fluid w-100"
                 @error="handleImageError">
         </div>

         <!-- Title Section -->
         <div class="px-3 pt-3" v-if="datarow?.name || datarow?.title || datarow?.companyname">
            <h2 class="fw-bold mb-2" v-if="datarow?.name">{{ datarow.name }}</h2>
            <h2 class="fw-bold mb-2" v-if="datarow?.title">{{ datarow.title }}</h2>
            <div class="d-flex align-items-center text-dark mb-3" v-if="datarow.companyname">
               <i class="bi bi-building me-2"></i>
               <span>{{ datarow.companyname }}</span>
            </div>
         </div>

         <!-- Key Details Card -->
         <div class="card key-details-card mx-3 mt-3" v-if="hasKeyDetails">
            <div class="card-body">
               <div class="row">
                  <!-- Cost/Price -->
                  <div class="col-6 " v-if="datarow.cost">
                     <div class="detail-item">
                        <div class="detail-icon bg-primary-light text-primary">
                           <i class="bi bi-currency-rupee"></i>
                        </div>
                        <div class="m-2">
                           <p class="detail-label">Price</p>
                           <p class="detail-value fw-bold">₹{{ formatNumber(datarow.cost) }}</p>
                        </div>
                     </div>
                  </div>

                  <!-- Area/Sqft -->
                  <div class="col-6" v-if="datarow.feet">
                     <div class="detail-item">
                        <div class="detail-icon bg-primary-light text-primary">
                           <i class="bi bi-rulers"></i>
                        </div>
                        <div class="m-2">
                           <p class="detail-label">Area</p>
                           <p class="detail-value fw-bold">{{ datarow.feet }} sq/ft</p>
                        </div>
                     </div>
                  </div>

                  <!-- Date/Time -->
                  <div :class="datarow.feet?'col-6  mt-3':'col-6   '" v-if="datarow.fromdate || datarow.todate">
                     <div class="detail-item">
                        <div class="detail-icon bg-primary-light text-primary">
                           <i class="bi bi-calendar-event"></i>
                        </div>
                        <div class="m-2">
                           <p class="detail-label">Date</p>
                           <p class="detail-value">
                              {{ datarow.fromdate ? 'From: ' + datarow.fromdate : '' }}
                              {{ datarow.todate ? 'To: ' + datarow.todate : '' }}
                           </p>
                        </div>
                     </div>
                  </div>

                  <!-- Time -->
                  <div class="col-6 mt-3" v-if="datarow.time">
                     <div class="detail-item">
                        <div class="detail-icon bg-primary-light text-primary">
                           <i class="bi bi-clock"></i>
                        </div>
                        <div class="m-2">
                           <p class="detail-label">Time</p>
                           <p class="detail-value">{{ datarow.time }}</p>
                        </div>
                     </div>
                  </div>
               </div>
            </div>
         </div>

         <!-- Location Card -->
         <div class="card mx-3 mt-3" v-if="datarow?.location || datarow?.joblocation || hasValidCoordinates">
            <div class="card-body">
               <div class="d-flex align-items-start">
                  <div class="detail-icon bg-primary-light text-primary me-3">
                     <i class="bi bi-geo-alt"></i>
                  </div>
                  <div class="flex-grow-1">
                     <h6 class="fw-bold mb-2">Location</h6>
                     <p class="mb-2" v-if="datarow.location">{{ datarow.location }}</p>
                     <p class="mb-2" v-if="datarow.joblocation">{{ datarow.joblocation }}</p>
                     
                     <button v-if="hasValidCoordinates" 
                             @click="openGoogleMaps" 
                             class="btn btn-sm btn-outline-primary mt-2">
                        <i class="bi bi-map me-1"></i> View on Map
                     </button>
                  </div>
               </div>
            </div>
         </div>

         <!-- Contact Card -->
         <div class="card mx-3 mt-3" v-if="datarow.contact || datarow.mobile || datarow.website">
            <div class="card-body">
               <h6 class="fw-bold mb-3">Contact Information</h6>
               
               <!-- Phone -->
               <div class="contact-item mb-3" v-if="datarow.contact || datarow.mobile">
                  <div class="d-flex align-items-center">
                     <div class="contact-icon bg-primary-light text-primary me-3">
                        <i class="bi bi-telephone"></i>
                     </div>
                     <div>
                        <a :href="'tel:' + (datarow.contact || datarow.mobile)" 
                           class="text-dark text-decoration-none">
                           {{ datarow.contact || datarow.mobile }}
                        </a>
                     </div>
                  </div>
               </div>
               
               <!-- Website -->
               <div class="contact-item" v-if="datarow.website">
                  <div class="d-flex align-items-center">
                     <div class="contact-icon bg-primary-light text-primary me-3">
                        <i class="bi bi-globe"></i>
                     </div>
                     <div>
                        <a :href="datarow.website" 
                           target="_blank" 
                           class="text-dark text-decoration-none text-truncate d-block" style="    text-wrap: auto;">
                           {{ datarow.website }}
                        </a>
                     </div>
                  </div>
               </div>
            </div>
         </div>

         <!-- Education/Requirements Card -->
         <div class="card mx-3 mt-3" v-if="datarow.educationreq || datarow.experienceyear">
            <div class="card-body">
               <h6 class="fw-bold mb-3">Requirements</h6>
               
               <div class="mb-2" v-if="datarow.educationreq">
                  <span class="fw-medium">Education:</span>
                  <span class="ms-2">{{ datarow.educationreq }}</span>
               </div>
               
               <div v-if="datarow.experienceyear">
                  <span class="fw-medium">Experience:</span>
                  <span class="ms-2">{{ datarow.experienceyear }} years</span>
               </div>
            </div>
         </div>

         <!-- Description Card -->
         <div class="card mx-3 mt-3" v-if="datarow.description">
            <div class="card-body">
               <h6 class="fw-bold mb-3">Description</h6>
               <p class="mb-0" style="white-space: pre-line">{{ datarow.description }}</p>
            </div>
         </div>
      </div>

      <!-- Share Footer -->
      <div class="share-footer bg-white border-top">
         <div class="d-flex justify-content-around py-2">
            <button v-for="(platform, index) in sharePlatforms" 
                    :key="index" 
                    @click="shareOnPlatform(platform)"
                    class="btn btn-link text-dark text-center share-button">
               <div class="share-icon mb-1" :class="'bg-' + platform.color + '-light'">
                  <i :class="'bi bi-' + platform.icon" :style="{ color: platform.color }"></i>
               </div>
               <span class="small">{{ platform.name }}</span>
            </button>
            
            <button @click="nativeShare" class="btn btn-link text-dark text-center share-button">
               <div class="share-icon mb-1 bg-primary">
                  <i class="bi bi-share text-white"></i>
               </div>
               <span class="small">Share</span>
            </button>
         </div>
      </div>
   </div>
</template>

 

<style scoped>
.listing-details-container {
   display: flex;
   flex-direction: column;
   min-height: 100vh;
   background-color: #f8f9fa;
}

.listing-header {
   display: flex;
   align-items: center;
   padding: 12px 16px;
   position: sticky;
   top: 0;
   z-index: 10;
}

.header-text {
   margin-left: 12px;
}

.listing-content {
   flex: 1;
   padding-bottom: 20px;
}

.listing-image-container {
   height: 250px;
   overflow: hidden;
   display: flex;
   align-items: center;
   justify-content: center;
   background-color: #e9ecef;
}

.listing-image-container img {
   object-fit: cover;
   width: 100%;
   height: 100%;
   transition: transform 0.3s ease;
}

.listing-image-container:hover img {
   transform: scale(1.02);
}

.card {
   border: none;
   border-radius: 12px;
   box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
   transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.card:hover {
   transform: translateY(-2px);
   box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.key-details-card {
   background-color: #fff;
}

.detail-item {
   display: flex;
   align-items: center;
}

.detail-icon {
   width: 36px;
   height: 36px;
   border-radius: 8px;
   display: flex;
   align-items: center;
   justify-content: center;
   flex-shrink: 0;
}

.detail-label {
   font-size: 12px;
   color: #6c757d;
   margin-bottom: 2px;
}

.detail-value {
   margin-bottom: 0;
   color: #212529;
}

.contact-icon {
   width: 32px;
   height: 32px;
   border-radius: 8px;
   display: flex;
   align-items: center;
   justify-content: center;
}

.share-footer {
   position: fixed;
   bottom: 0;
   left: 0;
   right: 0;
   z-index: 10;
   box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);
}

.share-button {
   flex: 1;
   text-decoration: none;
   padding: 8px 0;
}

.share-icon {
   width: 40px;
   height: 40px;
   border-radius: 50%;
   display: flex;
   align-items: center;
   justify-content: center;
   margin: 0 auto;
}

.bg-primary-light {
   background-color: rgba(13, 110, 253, 0.1);
}

.bg-success-light {
   background-color: rgba(25, 135, 84, 0.1);
}

.bg-info-light {
   background-color: rgba(13, 202, 240, 0.1);
}

/* Responsive adjustments */
@media (max-width: 576px) {
   .listing-image-container {
      height: 200px;
   }
   
   .detail-icon {
      width: 32px;
      height: 32px;
   }
}

/* Animation for cards */
@keyframes fadeInUp {
   from {
      opacity: 0;
      transform: translateY(10px);
   }
   to {
      opacity: 1;
      transform: translateY(0);
   }
}

.card {
   animation: fadeInUp 0.4s ease-out forwards;
}

.card:nth-child(1) { animation-delay: 0.1s; }
.card:nth-child(2) { animation-delay: 0.2s; }
.card:nth-child(3) { animation-delay: 0.3s; }
.card:nth-child(4) { animation-delay: 0.4s; }
</style>

<style scoped>
  .main_div{
    overflow-y: scroll !important;
    padding-bottom: 80px !important;
  }
img {
    
    /* height: 200px; */
    height: 350px;
    width:330px;

}
.weather-container {
  text-align: center;
    font-family: Arial, sans-serif;
    padding: 4px 20px;
    background: #f4f4f4;
    margin: auto;
    border-radius: 10px;
}
.bg_card{
  background: #ededec;
    margin: 10px 0px;
    padding-bottom: 10px;
}

.footer{
    position: fixed;
    z-index: 999;
    bottom: 0;
    width: -webkit-fill-available;
        background: linear-gradient(328deg, #006e24, #000199);
}
.custom-blue {
    color: blue !important;
}

</style>
 
<script> 
import axios from "@/plugins/axios";  
import { useRoute } from 'vue-router'; 


export default {
 
  name: 'PostView',
  components: { 
  },
  mounted() {
   

const route = useRoute();
// this.currentUrl = encodeURIComponent(window.location.origin + route.fullPath);//+"&name="+this.$route.query.name; 
// this.currentUrl=this.currentUrl.replace("singleeventlist","share");
const originalUrl = "https://mh13.in/app/index.php" + route.fullPath;
const shareUrl = originalUrl.replace("singleeventlist", "share");
this.currentUrl = encodeURIComponent(shareUrl);


                if (this.$route.query.id && this.$route.query.name) {
                    if(this.$route.query.name=="event"){
                        this.isEvent=true;
                        this.isProperties=false;
                        this.isFood=false;
                        this.isBusiness=false;
                        this.isJob=false;
                        this.isNews=false;
                    }else if(this.$route.query.name=="business"){
                        this.isEvent=false;
                        this.isProperties=false;
                        this.isFood=false;
                        this.isBusiness=true;
                        this.isJob=false;
                        this.isNews=false;
                    }else if(this.$route.query.name=="news"){
                        this.isEvent=false;
                        this.isProperties=false;
                        this.isFood=false;
                        this.isBusiness=false;
                        this.isJob=false;
                        this.isNews=true;
                    }else if(this.$route.query.name=="job"){
                        this.isEvent=false;
                        this.isProperties=false;
                        this.isFood=false;
                        this.isBusiness=false;
                        this.isJob=true;
                        this.isNews=false;
                    }else if(this.$route.query.name=="property"){
                        this.isEvent=false;
                        this.isProperties=true;
                        this.isFood=false;
                        this.isBusiness=false;
                        this.isJob=false;
                        this.isNews=false;
                    }
                    else if(this.$route.query.name=="food"){
                        this.isEvent=false;
                        this.isProperties=false;
                        this.isFood=true;
                        this.isBusiness=false;
                        this.isJob=false;
                        this.isNews=false;
                    }
                    
            
            
        this.getHomeBasic(this.$route.query.id, this.$route.query.name);
   }

        else{
        this.getHomeBasic();
        }

    // this.getHomeBasic(); // Call the method here


},
  computed: {
      hasKeyDetails() {
         return this.datarow.cost || this.datarow.feet || this.datarow.fromdate || this.datarow.todate || this.datarow.time;
      },
      hasValidCoordinates() {
         return this.datarow?.latitude && this.datarow?.longitude && 
                this.datarow.latitude.length > 4 && this.datarow.longitude.length > 4;
      },
      currentUrl() {
         return "https://mh13.in/app/index.php/share";//window.location.href;
      }
   },
  data() {
    return {
      loading:false,
      error:null,
      datarow:[],
       img_path:'',
       _name:'',
      isEvent:false,
      isBusiness:false,
      isProperties:false,
      isFood:false,
      isNews:false,
      isJob:false, 
      currentUrl:'',
       sharePlatforms: [
            { name: 'Facebook', icon: 'facebook', color: 'primary', url: 'https://www.facebook.com/sharer/sharer.php?u=' },
            { name: 'WhatsApp', icon: 'whatsapp', color: 'success', url: 'https://api.whatsapp.com/send?text=' },
            { name: 'Twitter', icon: 'twitter', color: 'info', url: 'https://twitter.com/intent/tweet?url=' }
         ]


    }
  },
  methods:{
      
      getOnMap(datarow){
           if(datarow?.latitude && datarow?.longitude && datarow.latitude.length>4 && datarow.longitude.length>4)
                       window.location='http://maps.google.com/maps?q='+datarow.latitude+'.'+datarow.longitude;
                     
      },
    
async fetchWeather() {
      const url =
        "https://api.open-meteo.com/v1/forecast?latitude=17.6599&longitude=75.9064&hourly=apparent_temperature&timezone=auto&forecast_days=1";

      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error("Weather data not available");
        const data = await response.json();
        
        // Get the latest hourly temperature
        if (data.hourly && data.hourly.apparent_temperature.length > 0) {
          this.current_temperature = data.hourly.apparent_temperature[0]; // First hour's temperature
        }
      } catch (error) {
        console.error("Error fetching weather:", error);
      }
    }, 
    updateMetaTags() {
     // || "MH13 City Directory";
      let title=this.datarow?.name;
      if(!title)
        title=this.datarow?.title; 
      
       document.title = title;
       
       
      const shortDesc = this.datarow.description.length > 160 ? this.datarow.description.slice(0, 157) + "..." : this.datarow.description;
      
      this.setMetaTag("property", "og:title", this.datarow.name);
      this.setMetaTag("property", "og:description", shortDesc);
      this.setMetaTag("property", "og:image", `${this.asset_path}${this.img_path}/${this.datarow.img}`);
      this.setMetaTag("property", "og:url", this.currentUrl);
      this.setMetaTag("property", "og:type", "website");
      this.setMetaTag("name", "twitter:card", "summary_large_image");
      this.setMetaTag("name", "twitter:title", this.datarow.name);
      this.setMetaTag("name", "twitter:description", shortDesc);
      this.setMetaTag("name", "twitter:image", `${this.asset_path}${this.img_path}/${this.datarow.img}`);
    },

    setMetaTag(attr, name, content) {
      let tag = document.querySelector(`meta[${attr}='${name}']`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attr, name);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content || "");
    },
    async getHomeBasic(id="",name="") {

     
        this.loading = true;
        this.error = null;
  
        try {
           var self=this;
             axios.post('singleeventlist',{_id:id,_name:name}).then((response) => {
            // self.categories = response.data.app_categories;
             self.datarow = response.data.datarow; 
             self.img_path = response.data.img_path; 
             self._name = response.data._name; 
            
            self.updateMetaTags();
            
        
          }).then(() => {
               self.initializeHomeContent();
          }).catch((err) => {
            
          });;
         

        } catch (err) {
          this.error = err.response?.data?.message || "Login failed. Please try again.";
        } finally {
          this.loading = false;
        }
      }, 
      
       shareNow() {
            if (navigator.share) {
              navigator.share({
                title: this.datarow?.name || 'Check this out',
                text: this.datarow?.description || '',
                url: this.currentUrl,
              }).then(() => {
                console.log('Thanks for sharing!');
              }).catch((error) => {
                console.error('Error sharing:', error);
              });
            } else {
              alert('Sharing is not supported on this browser.');
            }
          },
             handleImageError(e) {
             e.target.src = 'https://placehold.co/600x400?text=Image+Not+Available';
          },
      formatNumber(num) {
         return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      },
      openGoogleMaps() {
         if (this.hasValidCoordinates) {
            window.open(`http://maps.google.com/maps?q=${this.datarow.latitude},${this.datarow.longitude}`, '_blank');
         }
      },
      shareOnPlatform(platform) {
         window.open(platform.url +this.currentUrl, '_blank');
      },
      nativeShare() {
         if (navigator.share) {
            navigator.share({
               title: this.datarow.name || 'Check this listing',
               text: this.datarow.description ? this.datarow.description.substring(0, 100) + '...' : '',
               url: this.currentUrl
            }).catch(err => {
               console.log('Error sharing:', err);
            });
         } else {
            // Fallback for browsers that don't support Web Share API
            this.shareOnPlatform({ url: '' });
         }
      }
  }
}



</script>
